import React, { useState, useCallback } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Spin, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getCropNamesApi } from '../../api/crops';
import { AREA_UNITS, CROP_NAME_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const AREA_UNIT_OPTIONS = Object.entries(AREA_UNITS).map(([k, v]) => ({ value: k, label: v }));

export default function CropModal({ open, onClose, onSubmit, initialValues, farmId, selectedPlotId, selectedPlot, plots, loading }) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [cropOptions, setCropOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [commonId, setCommonId] = useState(null);
  const [harvestDate, setHarvestDate] = useState(null);
  const [sowingDate, setSowingDate] = useState(null);
  const [isHarvested, setIsHarvested] = useState(false);

  React.useEffect(() => {
    if (open) {
      if (initialValues) {
        const hd = initialValues.harvest_date ? dayjs(initialValues.harvest_date) : null;
        const sd = initialValues.crop_date ? dayjs(initialValues.crop_date) : null;
        const harvested = initialValues.status === 0 || initialValues.status === '0';
        setHarvestDate(hd);
        setSowingDate(sd);
        setIsHarvested(harvested);
        form.setFieldsValue({
          cropName: initialValues.crop_name || initialValues.crop,
          verity: initialValues.verity || '',
          area: initialValues.crop_area || initialValues.area,
          areaUnit: String(initialValues.crop_area_unit ?? initialValues.area_unit ?? '3'),
          date: initialValues.crop_date ? dayjs(initialValues.crop_date) : null,
          plotId: initialValues.plot_id || null,
          harvest_date: hd,
          status: harvested,
        });
        setCommonId(initialValues.common_id || null);
      } else {
        form.resetFields();
        form.setFieldsValue({
          plotId: selectedPlotId || undefined,
          areaUnit: String(selectedPlot?.seted_area_unit || '3'),
          area: selectedPlot?.seted_area || undefined,
        });
        setCommonId(null);
      }
    }
  }, [open, initialValues, form, selectedPlot, selectedPlotId]);

  const searchCrops = useCallback(async (val = '') => {
    setSearching(true);
    try {
      const res = await getCropNamesApi({ lang: i18n.language, crop: val });
      setCropOptions(
        (res.data || []).map((c) => ({
          value: c.name,
          label: c.name,        // plain string — shown in the trigger/search input
          commonId: c.common_id,
          image: c.image || null,
        }))
      );
    } catch {} finally { setSearching(false); }
  }, []);

  const handlePlotChange = useCallback((plotId) => {
    const plot = plots?.find((p) => p.id === plotId);
    if (plot) {
      form.setFieldsValue({
        area: plot.seted_area || undefined,
        areaUnit: String(plot.seted_area_unit || '3'),
      });
    }
  }, [plots, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSubmit({
        cropName: values.cropName,
        verity: values.verity || '',
        area: values.area,
        areaUnit: values.areaUnit || '3',
        farm: farmId,
        date: values.date ? values.date.format('YYYY-MM-DD') : '',
        plotId: selectedPlotId || values.plotId || null,
        hasPlot: false,
        plotName: '',
        plotPath: null,
        sqmArea: '',
        common_id: commonId,
        harvest_date: values.harvest_date ? values.harvest_date.format('YYYY-MM-DD') : '',
        status: values.status ? 0 : 1,
      });
    }).catch(() => {});
  };

  return (
    <Modal
      title={initialValues?.id ? 'Edit Crop' : t('add_crop')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>{t('cancel')}</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleOk}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('save')}</Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label={t('crop_name')} name="cropName" rules={[{ required: true, message: 'Select a crop' }]} style={{ marginBottom: 15 }}>
          {initialValues?.id ? (
            <Input disabled />
          ) : (
            <Select
              showSearch
              filterOption={false}
              onSearch={searchCrops}
              onFocus={() => { if (cropOptions.length === 0) searchCrops(''); }}
              onSelect={(_, opt) => setCommonId(opt.commonId)}
              options={cropOptions}
              optionRender={(opt) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {opt.data.image ? (
                    <img src={CROP_NAME_IMAGE(opt.data.image)} alt={opt.data.label} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 28, textAlign: 'center', flexShrink: 0 }}>🌱</span>
                  )}
                  <span>{opt.data.label}</span>
                </div>
              )}
              placeholder="Search or select crop..."
              notFoundContent={searching ? <Spin size="small" /> : 'No crops found'}
              listHeight={320}
            />
          )}
        </Form.Item>

        <Form.Item label="Variety" name="verity" style={{ marginBottom: 15 }}>
          <Input placeholder="e.g. BT, Hybrid, Local..." />
        </Form.Item>

        {plots?.length > 0 && (
          <Form.Item label="Plot" name="plotId" style={{ marginBottom: 15 }}>
            <Select
              allowClear
              placeholder="Select plot (optional)"
              options={plots.map((p) => ({ value: p.id, label: p.name }))}
              onChange={handlePlotChange}
            />
          </Form.Item>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 15 }}>
          <Form.Item label={t('area')} name="area" rules={[{ required: true, message: 'Enter area' }]} style={{ marginBottom: 0 }}>
            <Input type="number" min={0} placeholder="e.g. 2" />
          </Form.Item>
          <Form.Item label={t('area_unit')} name="areaUnit" style={{ marginBottom: 0 }}>
            <Select options={AREA_UNIT_OPTIONS} />
          </Form.Item>
        </div>

        <Form.Item label={t('sowing_date')} name="date" rules={[{ required: true, message: 'Select sowing date' }]} style={{ marginBottom: 15 }}>
          <DatePicker
            style={{ width: '100%' }}
            onChange={(d) => {
              setSowingDate(d);
              setHarvestDate(null);
              form.setFieldsValue({ harvest_date: null, status: false });
            }}
          />
        </Form.Item>

        <Form.Item label="Expected Harvest Date" name="harvest_date" rules={[{ required: true, message: 'Select expected harvest date' }]} style={{ marginBottom: 15 }}>
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(d) => sowingDate && d && !d.isAfter(sowingDate, 'day')}
            onChange={(d) => {
              setHarvestDate(d);
              if (!d) form.setFieldsValue({ status: false });
            }}
          />
        </Form.Item>

        {initialValues?.id && (
          <Form.Item>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>Mark as Harvested</div>
                {!harvestDate && !isHarvested && (
                  <div style={{ fontSize: 12, color: '#faad14', marginTop: 2 }}>Please select harvest date to enable mark as harvest</div>
                )}
              </div>
              <Form.Item name="status" valuePropName="checked" noStyle>
                <Switch
                  disabled={!harvestDate && !isHarvested}
                  checkedChildren="Harvested"
                  unCheckedChildren="Active"
                  className="harvest-switch"
                />
              </Form.Item>
            </div>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
